/**
 * Canva Connect OAuth 2.0 with PKCE flow.
 * https://www.canva.dev/docs/connect/authentication/
 */

const CANVA_AUTH_URL = 'https://www.canva.com/api/oauth/authorize'
const STORAGE_KEY_VERIFIER = 'canva_code_verifier'
const STORAGE_KEY_TOKEN = 'canva_access_token'
const STORAGE_KEY_REFRESH = 'canva_refresh_token'
const STORAGE_KEY_EXPIRES = 'canva_token_expires'

function generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
    const values = crypto.getRandomValues(new Uint8Array(length))
    return Array.from(values, (v) => charset[v % charset.length]).join('')
}

async function sha256(plain: string): Promise<ArrayBuffer> {
    const encoder = new TextEncoder()
    const data = encoder.encode(plain)
    return crypto.subtle.digest('SHA-256', data)
}

function base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    bytes.forEach((b) => (binary += String.fromCharCode(b)))
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Starts the Canva OAuth flow by redirecting to Canva's authorization page.
 */
export async function startCanvaAuth(): Promise<void> {
    const clientId = import.meta.env.VITE_CANVA_CLIENT_ID
    if (!clientId) {
        throw new Error('VITE_CANVA_CLIENT_ID not configured')
    }

    const codeVerifier = generateRandomString(64)
    localStorage.setItem(STORAGE_KEY_VERIFIER, codeVerifier)

    const codeChallenge = base64UrlEncode(await sha256(codeVerifier))
    const redirectUri = `${window.location.origin}/canva/callback`

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        scope: 'design:meta:read design:content:read',
    })

    window.location.href = `${CANVA_AUTH_URL}?${params.toString()}`
}

/**
 * Gets the stored code verifier for the token exchange.
 */
export function getCodeVerifier(): string | null {
    return localStorage.getItem(STORAGE_KEY_VERIFIER)
}

/**
 * Gets the redirect URI used in the auth flow.
 */
export function getRedirectUri(): string {
    return `${window.location.origin}/canva/callback`
}

/**
 * Stores Canva tokens after successful exchange.
 */
export function storeCanvaTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
    localStorage.setItem(STORAGE_KEY_TOKEN, accessToken)
    localStorage.setItem(STORAGE_KEY_REFRESH, refreshToken)
    localStorage.setItem(STORAGE_KEY_EXPIRES, String(Date.now() + expiresIn * 1000))
    localStorage.removeItem(STORAGE_KEY_VERIFIER)
}

/**
 * Gets the stored Canva access token, or null if expired/missing.
 */
export function getCanvaAccessToken(): string | null {
    const token = localStorage.getItem(STORAGE_KEY_TOKEN)
    const expires = localStorage.getItem(STORAGE_KEY_EXPIRES)
    if (!token || !expires) return null
    if (Date.now() > Number(expires)) return null
    return token
}

/**
 * Gets the stored Canva refresh token.
 */
export function getCanvaRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEY_REFRESH)
}

/**
 * Checks if the user has connected their Canva account.
 */
export function isCanvaConnected(): boolean {
    return !!getCanvaAccessToken() || !!getCanvaRefreshToken()
}

/**
 * Clears all Canva auth data.
 */
export function disconnectCanva(): void {
    localStorage.removeItem(STORAGE_KEY_TOKEN)
    localStorage.removeItem(STORAGE_KEY_REFRESH)
    localStorage.removeItem(STORAGE_KEY_EXPIRES)
    localStorage.removeItem(STORAGE_KEY_VERIFIER)
}
